/**
 * Dashboard Page - Main KPIs
 */

import { useSession } from "@/state/session";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, Package, Factory, AlertTriangle } from "lucide-react";
import { Layout } from "@/components/Layout";
import { useQuery } from "@tanstack/react-query";
import { productsAdapter } from "@/data";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

export default function Dashboard() {
  const { user } = useSession();

  const { data: produtosData, isLoading: isProdutosLoading } = useQuery({
    queryKey: ["produtos-count"],
    queryFn: async () => {
      const response = await productsAdapter.list({ limit: 1 });
      return response.total;
    },
  });

  const stats = [
    {
      title: "Vendas Hoje",
      value: "R$ 2.450,00",
      icon: TrendingUp,
      trend: "+12%",
      color: "text-success",
    },
    {
      title: "Estoque Crítico",
      value: "3 itens",
      icon: AlertTriangle,
      trend: "Atenção",
      color: "text-warning",
    },
    {
      title: "Fornadas Abertas",
      value: "2",
      icon: Factory,
      trend: "Em produção",
      color: "text-primary",
    },
    {
      title: "Produtos Cadastrados",
      value: isProdutosLoading ? "..." : (produtosData?.toString() || "0"),
      icon: Package,
      trend: "Catálogo",
      color: "text-accent",
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold mb-2">Painel de Controle</h1>
          <p className="text-muted-foreground">
            Bem-vindo, {user?.name}! Aqui está o resumo do seu negócio.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.title} className="shadow-soft hover:shadow-medium transition-smooth">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stat.trend}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle>Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <a
                href="/vendas/nova"
                className="p-4 rounded-lg border border-border hover:bg-accent/50 transition-fast cursor-pointer"
              >
                <h3 className="font-semibold mb-1">Nova Venda</h3>
                <p className="text-sm text-muted-foreground">
                  Registrar venda no POS
                </p>
              </a>
              <a
                href="/producao/fornadas/nova"
                className="p-4 rounded-lg border border-border hover:bg-accent/50 transition-fast cursor-pointer"
              >
                <h3 className="font-semibold mb-1">Nova Fornada</h3>
                <p className="text-sm text-muted-foreground">
                  Iniciar produção
                </p>
              </a>
              <a
                href="/estoque"
                className="p-4 rounded-lg border border-border hover:bg-accent/50 transition-fast cursor-pointer"
              >
                <h3 className="font-semibold mb-1">Ver Estoque</h3>
                <p className="text-sm text-muted-foreground">
                  Consultar disponibilidade
                </p>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle>Atividade Recente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <div className="w-2 h-2 mt-2 rounded-full bg-success" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Venda registrada</p>
                  <p className="text-xs text-muted-foreground">
                    2 Pudins M - R$ 80,00 • há 15 minutos
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <div className="w-2 h-2 mt-2 rounded-full bg-primary" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Fornada concluída</p>
                  <p className="text-xs text-muted-foreground">
                    12 Pudins P produzidos • há 1 hora
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <div className="w-2 h-2 mt-2 rounded-full bg-warning" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Alerta de estoque</p>
                  <p className="text-xs text-muted-foreground">
                    Açúcar está abaixo do mínimo • há 2 horas
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
